import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import axios from 'axios';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface LanguageContextType {
  currentLanguage: SupportedLocale;
  changeLanguage: (language: SupportedLocale) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const LanguageProvider = ({ children }: React.PropsWithChildren) => {
  const { i18n } = useTranslation();

  const [currentLanguage, setCurrentLanguage] = useState<SupportedLocale>(
    () => {
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage) {
        const lang = savedLanguage as SupportedLocale;
        if (lang !== (i18n.language as SupportedLocale)) {
          i18n.changeLanguage(lang);
        }
        return lang;
      }
      return i18n.language as SupportedLocale;
    },
  );

  const parsedCurrentLanguage = useMemo(
    () => currentLanguage.slice(0, 2) as SupportedLocale,
    [currentLanguage],
  );

  const changeLanguage = useCallback(
    (language: SupportedLocale) => {
      i18n.changeLanguage(language);
      setCurrentLanguage(language);
      localStorage.setItem('language', language);
    },
    [i18n],
  );

  useEffect(() => {
    axios.defaults.headers.common['Accept-Language'] = currentLanguage
      .slice(0, 2)
      .toUpperCase();
  }, [currentLanguage]);

  return (
    <LanguageContext.Provider
      value={{ currentLanguage: parsedCurrentLanguage, changeLanguage }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

// oxlint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
  const context = use(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
